import {useState} from "react";
import {App as AntdApp, Input, Modal, Table, Tag, Tooltip} from "antd";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetWebhooksQuery,
	useCreateWebhookMutation,
	useUpdateWebhookMutation,
	useDeleteWebhookMutation,
	useTestWebhookMutation,
	useGetWebhookDeliveriesQuery,
} from "~/reduxs/api/apiAccessApiSlice";
import WebhookFormModal from "./WebhookFormModal";

const DELIVERY_STATUS = {
	pending: {color: 'blue', label: 'Đang chờ'},
	sending: {color: 'blue', label: 'Đang gửi'},
	sent: {color: 'green', label: 'Đã gửi'},
	failed: {color: 'red', label: 'Thất bại'},
};

/** Modal nhật ký 50 lần gửi gần nhất của 1 webhook (fetch lazy khi mở). */
function DeliveriesModal({hook, onClose}) {

	const {data: rows = [], isFetching} = useGetWebhookDeliveriesQuery(hook.id);

	const columns = [
		{title: 'Sự kiện', dataIndex: 'event', key: 'event', render: (v) => <code>{v}</code>},
		{
			title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 110,
			render: (s) => {
				const m = DELIVERY_STATUS[s] || {color: 'default', label: s};
				return <Tag color={m.color}>{m.label}</Tag>;
			},
		},
		{title: 'HTTP', dataIndex: 'response_code', key: 'response_code', width: 70, render: (v) => v || '—'},
		{title: 'Lần thử', dataIndex: 'attempts', key: 'attempts', width: 80},
		{title: 'Lỗi gần nhất', dataIndex: 'last_error', key: 'last_error', ellipsis: true, responsive: ['md'], render: (v) => v || '—'},
		{title: 'Thời gian', dataIndex: 'created', key: 'created', width: 160, responsive: ['md']},
	];

	return (
		<Modal open onCancel={onClose} footer={null} width={860} title={`Nhật ký gửi — ${hook.name || hook.url}`}>
			{isFetching ? <Loading noFixed /> : (
				<Table
					rowKey="id"
					columns={columns}
					dataSource={rows}
					locale={{emptyText: 'Chưa có lần gửi nào — thử nút "Gửi thử" hoặc chờ sự kiện tiếp theo'}}
					pagination={{pageSize: 10, hideOnSinglePage: true}}
					scroll={{x: 'max-content'}}
					size="small"
				/>
			)}
		</Modal>
	);
}

/**
 * Tab "Webhooks" — CRUD webhook + gửi thử + xem nhật ký + secret ký HMAC.
 * Webhook lỗi 10 lần liên tiếp bị hệ thống tự tắt (status=disabled) — sửa lại là bật.
 */
function WebhooksPanel({canApi}) {

	const {notification, modal} = AntdApp.useApp();

	const {data, isLoading} = useGetWebhooksQuery();
	const webhooks = data?.webhooks || [];
	const events = data?.events || {};

	const [createWebhook, {isLoading: creating}] = useCreateWebhookMutation();
	const [updateWebhook, {isLoading: updating}] = useUpdateWebhookMutation();
	const [deleteWebhook] = useDeleteWebhookMutation();
	const [testWebhook] = useTestWebhookMutation();

	// null = đóng; {} = tạo mới; {...row} = sửa.
	const [editing, setEditing] = useState(null);
	const [secretOf, setSecretOf] = useState(null);   // webhook đang xem secret
	const [logsOf, setLogsOf] = useState(null);       // webhook đang xem nhật ký

	const onSubmit = async (values) => {
		try {
			if (editing?.id) {
				await updateWebhook({id: editing.id, ...values}).unwrap();
				notification.success({message: 'Đã cập nhật webhook'});
			} else {
				await createWebhook(values).unwrap();
				notification.success({message: 'Đã tạo webhook', description: 'Sự kiện mới sẽ được gửi tới URL này kể từ bây giờ.'});
			}
			setEditing(null);
		} catch (e) {
			notification.error({message: 'Lưu webhook thất bại', description: rtkErrorMessage(e)});
		}
	};

	const handleDisable = async (row) => {
		try {
			await updateWebhook({id: row.id, status: 'disabled'}).unwrap();
			notification.success({message: 'Đã tắt webhook'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const handleDelete = (row) => {
		modal.confirm({
			title: 'Xoá webhook',
			content: `Xoá webhook "${row.name || row.url}" cùng toàn bộ nhật ký gửi?`,
			okText: 'Xoá',
			okButtonProps: {danger: true},
			cancelText: 'Hủy',
			onOk: async () => {
				try {
					await deleteWebhook(row.id).unwrap();
					notification.success({message: 'Đã xoá webhook'});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Xoá thất bại')});
				}
			},
		});
	};

	const handleTest = async (row) => {
		try {
			await testWebhook(row.id).unwrap();
			notification.success({message: 'Đã xếp sự kiện thử nghiệm', description: 'Webhook sẽ nhận POST trong khoảng 1 phút — xem kết quả ở Nhật ký gửi.'});
		} catch (e) {
			notification.error({message: 'Gửi thử thất bại', description: rtkErrorMessage(e)});
		}
	};

	const copySecret = async () => {
		try {
			await navigator.clipboard.writeText(secretOf.secret);
			notification.success({message: 'Đã copy secret'});
		} catch (e) {
			notification.error({message: 'Không copy được — hãy bôi đen và copy thủ công'});
		}
	};

	const columns = [
		{
			title: 'Webhook', key: 'name',
			render: (_, r) => (
				<div>
					<strong>{r.name || '(không tên)'}</strong>
					<div style={{fontSize: 12.5, color: 'var(--gray-500, #888)', wordBreak: 'break-all'}}>{r.url}</div>
				</div>
			),
		},
		{
			title: 'Sự kiện', dataIndex: 'events', key: 'events', width: 220, responsive: ['md'],
			render: (list) => !list?.length
				? <Tag>Tất cả</Tag>
				: list.map((e) => <Tag key={e} color="blue">{events[e] || e}</Tag>),
		},
		{
			title: 'Trạng thái', key: 'status', width: 140,
			render: (_, r) => r.status === 'active'
				? <Tag color="green">Hoạt động</Tag>
				: <Tag color="red">Đã tắt{r.fail_streak >= 10 ? ' (lỗi liên tiếp)' : ''}</Tag>,
		},
		{
			title: 'Gửi gần nhất', key: 'last', width: 170, responsive: ['lg'],
			render: (_, r) => r.last_sent_at
				? <span>{r.last_sent_at} <Tag color={r.last_status >= 200 && r.last_status < 300 ? 'green' : 'red'}>{r.last_status}</Tag></span>
				: '—',
		},
		{
			title: '', key: 'actions', width: 230,
			render: (_, r) => (
				<div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
					<Tooltip title="Gửi thử">
						<span><Button small blue disabled={!canApi || r.status !== 'active'} onClick={() => handleTest(r)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-paper-plane" />} /></span>
					</Tooltip>
					<Tooltip title="Nhật ký gửi">
						<Button small outline onClick={() => setLogsOf(r)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-list-timeline" />} />
					</Tooltip>
					<Tooltip title="Secret ký HMAC">
						<Button small outline onClick={() => setSecretOf(r)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-key" />} />
					</Tooltip>
					<Tooltip title={r.status === 'active' ? 'Sửa' : 'Sửa & bật lại'}>
						<span><Button small outline disabled={!canApi}
							onClick={() => setEditing({id: r.id, name: r.name, url: r.url, events: r.events})}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />} /></span>
					</Tooltip>
					{r.status === 'active' && (
						<Tooltip title="Tắt (giữ cấu hình)">
							<Button small outline onClick={() => handleDisable(r)}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-pause" />} />
						</Tooltip>
					)}
					<Tooltip title="Xoá">
						<Button small red onClick={() => handleDelete(r)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
					</Tooltip>
				</div>
			),
		},
	];

	return (
		<div>
			<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 14}}>
				<Tooltip title={!canApi ? 'Gói hiện tại không hỗ trợ API hệ thống' : ''}>
					<span>
						<Button primary disabled={!canApi} onClick={() => setEditing({})}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
							Tạo webhook
						</Button>
					</span>
				</Tooltip>
			</div>

			<Table
				rowKey="id"
				columns={columns}
				dataSource={webhooks}
				loading={isLoading}
				locale={{emptyText: 'Chưa có webhook nào — tạo webhook để nhận sự kiện (crawl xong, token sắp cạn...) vào hệ thống của bạn'}}
				pagination={false}
				scroll={{x: 'max-content'}}
			/>

			<WebhookFormModal
				open={editing !== null}
				title={editing?.id ? 'Sửa webhook' : 'Tạo webhook'}
				submitting={creating || updating}
				defaultValues={editing || {}}
				events={events}
				onCancel={() => setEditing(null)}
				onSubmit={onSubmit}
			/>

			<Modal
				open={!!secretOf}
				onCancel={() => setSecretOf(null)}
				footer={null}
				width={620}
				title="Secret ký HMAC"
			>
				<p style={{marginBottom: 10, fontSize: 13.5}}>
					Mỗi request gửi kèm header <code>X-OmniSeo-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</code> với{' '}
					<code>v1 = HMAC-SHA256(t + "." + rawBody, secret)</code> — xem cách xác minh ở tab Tài liệu API.
				</p>
				<div style={{display: 'flex', gap: 8}}>
					<Input readOnly value={secretOf?.secret || ''} style={{fontFamily: 'monospace'}} />
					<Button primary onClick={copySecret} leftIcon={<FontAwesomeIcon icon="fa-light fa-copy" />}>Copy</Button>
				</div>
			</Modal>

			{logsOf && <DeliveriesModal hook={logsOf} onClose={() => setLogsOf(null)} />}
		</div>
	);
}

export default WebhooksPanel;
